import type { ReactNode } from 'react';
import { ShieldOff } from 'lucide-react';
import type { Permission } from '@shared/permissions';
import { usePermission } from '@/hooks/usePermission';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';

/**
 * A page only some roles may use. Others reach it only by a link (the nav hides it), and see
 * why it's closed rather than being sent somewhere else (decision 6 in the T-150 audit). The
 * Worker enforces the same permission on the page's writes.
 */
export function RequirePermission({
  permission,
  what,
  children,
}: {
  permission: Permission;
  /** The page's name, as the sidebar or menu shows it ("Integrations"). */
  what: string;
  children: ReactNode;
}) {
  const allowed = usePermission(permission);
  if (allowed) return <>{children}</>;
  return <NoAccess what={what} />;
}

export function NoAccess({
  what,
  header = true,
}: {
  what: string;
  /** Set the top bar's breadcrumb to the page name (off in the /dev/ui gallery). */
  header?: boolean;
}) {
  return (
    <div className="space-y-6">
      {header ? <PageHeader title={what} /> : null}
      <EmptyState
        icon={<ShieldOff className="size-5" />}
        title={`You don't have access to ${what}`}
        description="Your role can't use this page. Ask an admin if you need it."
      />
    </div>
  );
}
